import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/Header'
import { useColors, formatCurrency } from '@/lib/utils'
import { useThemeStore } from '@/stores/theme'
import { ArrowUpRight, ArrowDownRight, FileText, Download, Table } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
})

// Mock data matching original
const salesData = [
  { date: '01', current: 65000, previous: 58000 },
  { date: '02', current: 72000, previous: 62000 },
  { date: '03', current: 68000, previous: 65000 },
  { date: '04', current: 75000, previous: 68000 },
  { date: '05', current: 82000, previous: 72000 },
  { date: '06', current: 78000, previous: 75000 },
  { date: '07', current: 85000, previous: 78000 },
]

const transactionData = [
  { date: '01', transactions: 1250 },
  { date: '02', transactions: 1380 },
  { date: '03', transactions: 1290 },
  { date: '04', transactions: 1420 },
  { date: '05', transactions: 1580 },
  { date: '06', transactions: 1490 },
  { date: '07', transactions: 1650 },
]

function DashboardPage() {
  const { isDarkMode } = useThemeStore()
  const Colors = useColors(isDarkMode)

  const handleDownload = (reportType: string, format: string) => {
    console.log(`Downloading ${reportType} in ${format} format`)
  }

  return (
    <div className="flex-1">
      <Header 
        title="Dashboard"
        subtitle="Performance at a glance"
        onOpenFilters={() => {}}
      />

      <div className="p-6 space-y-6">
        {/* Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div 
            className="p-6 rounded-xl shadow-sm"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <p className="text-sm font-inter-medium mb-1" style={{ color: Colors.text.secondary }}>
              Total Sales
            </p>
            <p className="text-2xl font-inter-bold mb-2" style={{ color: Colors.text.primary }}>
              {formatCurrency(128459)}
            </p>
            <div className="flex items-center space-x-1">
              <ArrowUpRight size={16} color={Colors.success.main} />
              <span className="text-sm font-inter-medium" style={{ color: Colors.success.main }}>
                8.2% vs last week
              </span>
            </div>
          </div>

          <div 
            className="p-6 rounded-xl shadow-sm"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <p className="text-sm font-inter-medium mb-1" style={{ color: Colors.text.secondary }}>
              Transactions
            </p>
            <p className="text-2xl font-inter-bold mb-2" style={{ color: Colors.text.primary }}>
              2,846
            </p>
            <div className="flex items-center space-x-1">
              <ArrowUpRight size={16} color={Colors.success.main} />
              <span className="text-sm font-inter-medium" style={{ color: Colors.success.main }}>
                4.5% vs last week
              </span>
            </div>
          </div>

          <div 
            className="p-6 rounded-xl shadow-sm"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <p className="text-sm font-inter-medium mb-1" style={{ color: Colors.text.secondary }}>
              Average Check
            </p>
            <p className="text-2xl font-inter-bold mb-2" style={{ color: Colors.text.primary }}>
              {formatCurrency(45.13)}
            </p>
            <div className="flex items-center space-x-1">
              <ArrowDownRight size={16} color={Colors.error.main} />
              <span className="text-sm font-inter-medium" style={{ color: Colors.error.main }}>
                2.1% vs last week
              </span>
            </div>
          </div>

          <div 
            className="p-6 rounded-xl shadow-sm"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <p className="text-sm font-inter-medium mb-1" style={{ color: Colors.text.secondary }}>
              Labor Cost
            </p>
            <p className="text-2xl font-inter-bold mb-2" style={{ color: Colors.text.primary }}>
              23.4%
            </p>
            <div className="flex items-center space-x-1">
              <ArrowDownRight size={16} color={Colors.success.main} />
              <span className="text-sm font-inter-medium" style={{ color: Colors.success.main }}>
                1.2% vs target
              </span>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div 
            className="p-6 rounded-xl shadow-sm"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <h3 className="text-lg font-inter-bold mb-4" style={{ color: Colors.text.primary }}>
              Sales Trend
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke={Colors.border.main} />
                <XAxis dataKey="date" stroke={Colors.text.secondary} />
                <YAxis 
                  tickFormatter={(value) => `$${value / 1000}k`} 
                  stroke={Colors.text.secondary}
                />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), '']}
                  labelFormatter={(label) => `Day ${label}`}
                  contentStyle={{
                    backgroundColor: Colors.background.paper,
                    border: `1px solid ${Colors.border.main}`,
                    borderRadius: '8px',
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="current" 
                  stroke={Colors.primary.main}
                  strokeWidth={2}
                  name="Current Year"
                />
                <Line 
                  type="monotone" 
                  dataKey="previous" 
                  stroke={Colors.neutral[400]}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="Previous Year"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div 
            className="p-6 rounded-xl shadow-sm"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <h3 className="text-lg font-inter-bold mb-4" style={{ color: Colors.text.primary }}>
              Daily Transactions
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={transactionData}>
                <CartesianGrid strokeDasharray="3 3" stroke={Colors.border.main} />
                <XAxis dataKey="date" stroke={Colors.text.secondary} />
                <YAxis stroke={Colors.text.secondary} />
                <Tooltip 
                  formatter={(value: number) => [value.toLocaleString(), 'Transactions']}
                  labelFormatter={(label) => `Day ${label}`}
                  contentStyle={{
                    backgroundColor: Colors.background.paper,
                    border: `1px solid ${Colors.border.main}`,
                    borderRadius: '8px',
                  }}
                />
                <Bar 
                  dataKey="transactions" 
                  fill={Colors.primary.main}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Reports Download Section */}
        <div 
          className="p-6 rounded-xl shadow-sm"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <h3 className="text-lg font-inter-bold mb-6" style={{ color: Colors.text.primary }}>
            Download Reports
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Sales Comparison Report */}
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: Colors.background.default }}
            >
              <div className="flex items-center space-x-2 mb-2">
                <FileText size={24} color={Colors.primary.main} />
                <h4 className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                  Sales Comparison
                </h4>
              </div>
              <p className="text-sm font-inter-regular mb-4" style={{ color: Colors.text.secondary }}>
                Compare sales data between current and previous periods
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDownload('sales-comparison', 'excel')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Table size={16} />
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => handleDownload('sales-comparison', 'pdf')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Download size={16} />
                  <span>PDF</span>
                </button>
              </div>
            </div>

            {/* Transactions Report */}
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: Colors.background.default }}
            >
              <div className="flex items-center space-x-2 mb-2">
                <FileText size={24} color={Colors.primary.main} />
                <h4 className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                  Transactions
                </h4>
              </div>
              <p className="text-sm font-inter-regular mb-4" style={{ color: Colors.text.secondary }}>
                Detailed transaction data with trends and patterns
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDownload('transactions', 'excel')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Table size={16} />
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => handleDownload('transactions', 'pdf')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Download size={16} />
                  <span>PDF</span>
                </button>
              </div>
            </div>

            {/* Average Check Report */}
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: Colors.background.default }}
            >
              <div className="flex items-center space-x-2 mb-2">
                <FileText size={24} color={Colors.primary.main} />
                <h4 className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                  Average Check
                </h4>
              </div>
              <p className="text-sm font-inter-regular mb-4" style={{ color: Colors.text.secondary }}>
                Analysis of average check values and trends
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDownload('average-check', 'excel')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Table size={16} />
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => handleDownload('average-check', 'pdf')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Download size={16} />
                  <span>PDF</span>
                </button>
              </div>
            </div>

            {/* Labor Cost Report */}
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: Colors.background.default }}
            >
              <div className="flex items-center space-x-2 mb-2">
                <FileText size={24} color={Colors.primary.main} />
                <h4 className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                  Labor Cost
                </h4>
              </div>
              <p className="text-sm font-inter-regular mb-4" style={{ color: Colors.text.secondary }}>
                Compare actual labor costs against matrix allowance
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDownload('labor-cost', 'excel')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Table size={16} />
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => handleDownload('labor-cost', 'pdf')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Download size={16} />
                  <span>PDF</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}